import { z } from 'zod';
import { ActForPerson, ActForRole, ActingFor } from './acting-for-base.js';
import { Cents, PlainDate } from './common.js';
import { PageInfo, pageFields } from './paging.js';

/** Phase 1 award types entered on Recognize (Spot nominations have their own flow). */
export const AwardTypeCode = z.enum(['SDB', 'EDB', 'PAA', 'TAA']);
export type AwardTypeCode = z.infer<typeof AwardTypeCode>;

/** Saved financial and fulfillment records include privately approved Spot cash awards. */
export const OperationalAwardTypeCode = AwardTypeCode.or(z.literal('SPOT'));
export type OperationalAwardTypeCode = z.infer<typeof OperationalAwardTypeCode>;

const Ref = z.object({ id: z.string(), name: z.string() });
const PersonRef = z.object({ id: z.string(), name: z.string(), jobTitle: z.string().nullable() });
const Units = z.object({ cashCents: Cents, options: z.number().int() });
const Count = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);

/**
 * Who gets the letters (PRD §11, APP-057): chosen by whoever enters the award, one choice for the
 * batch, changeable per person.
 * - `employee`: each person gets their own.
 * - `requester`: whoever entered the award ("Me").
 * - `manager`: each person's supervisor in the directory.
 * - `delegate`: the Sector Lead or one of the sector's delegates (`personId`).
 * - `person`: anyone else at Areté (`personId`).
 */
export const LetterRecipientRule = z.enum([
  'employee',
  'requester',
  'manager',
  'delegate',
  'person',
]);
export type LetterRecipientRule = z.infer<typeof LetterRecipientRule>;

/** The rules an award type's default can name (Settings › Elections & letters). */
export const LetterDefaultRule = z.enum(['requester', 'manager', 'employee']);
export type LetterDefaultRule = z.infer<typeof LetterDefaultRule>;

/** The batch choice as entered. `personId` is required by submit for `delegate` and `person`. */
export const DraftLetters = z
  .object({ rule: LetterRecipientRule, personId: z.uuid().nullable() })
  .strict();
export type DraftLetters = z.infer<typeof DraftLetters>;

/** APP-214: the most a reason for someone from another sector holds: one line. */
export const OTHER_SECTOR_REASON_MAX = 200;

/**
 * APP-214: why someone from another sector is on a Sector Discretionary Bonus, in one line: no
 * control characters (so no line breaks), and no bidirectional marks, which could make it read
 * differently on the record from what was typed. Empty is none.
 */
export const OtherSectorReason = z
  .string()
  .trim()
  .max(OTHER_SECTOR_REASON_MAX)
  .refine((value) => !/[\p{Cc}\p{Cs}\u202A-\u202E\u2066-\u2069]/u.test(value), {
    message: 'Use one line of letters, numbers and punctuation.',
  });

/** One person on a draft, as entered. The server works out everything else. */
export const DraftLine = z
  .object({
    personId: z.uuid(),
    /** SDB, EDB, TAA, and PAA since APP-151 (its amount is the cash): cash entered for this person. */
    cashCents: Count.optional(),
    /** SDB and EDB: options entered for this person. */
    options: Count.optional(),
    /**
     * PAA whose award is worked out the older way (`paaAmount: 'total_value'`): the total value,
     * split by the award's saved share. A PAA since APP-151 sends its amount as `cashCents`.
     */
    totalValueCents: Count.optional(),
    /** TAA in manual funding mode: the pool this person is paid from. */
    poolId: z.string().min(1).optional(),
    /** "Change for one person": this person's letter goes to someone other than the batch choice. */
    letterRecipientId: z.uuid().optional(),
    /**
     * APP-214, a Sector Discretionary Bonus: why this person, from another sector, is on it. Needed
     * to send it, not to save it; kept only while they're from another sector. Omitted on a save,
     * the line keeps the reason it has.
     */
    otherSectorReason: OtherSectorReason.optional(),
  })
  .strict();
export type DraftLine = z.infer<typeof DraftLine>;

const text = (max: number) => z.string().trim().max(max).nullable().optional();

/**
 * What shows as nothing: the domain's `withoutHidden` says the same, and contracts can't import it.
 * `letter-wording-agreement.test.ts` holds the two together.
 */
const HIDDEN = /[\p{Cf}\p{Default_Ignorable_Code_Point}]/gu;

/**
 * APP-201: words printed in every letter on the award: no control characters, as a citation, and
 * no bidirectional embedding, override or isolate marks, which could make the letter read
 * differently from what the form showed. SENT-256: nor only invisible characters, which would print
 * a blank. Blank itself is fine where it means the default, and a joiner inside real words stays.
 */
export const LetterWordingText = z
  .string()
  .trim()
  .max(300)
  .refine(
    (value) =>
      !/[\p{Cc}\p{Cs}\u202A-\u202E\u2066-\u2069]/u.test(value) &&
      (value === '' || value.replace(HIDDEN, '').trim() !== ''),
    { message: 'Use letters, numbers and punctuation only.' },
  );

const DraftBody = z
  .object({
    /** The sector the award is for. Required except for Executive Discretionary. */
    sectorId: z.string().min(1).nullable(),
    programId: z.uuid().nullable().optional(),
    /**
     * APP-270: the letter's wording when none is typed, so it refuses what every letter text refuses
     * (SENT-252). The engine would refuse it later, leaving the letter stuck on a name that can't be
     * edited after Draft.
     */
    title: LetterWordingText.pipe(z.string().max(200)).nullable().optional(),
    /**
     * APP-201: the letter's wording for everyone on the award. Null or empty follows the default
     * (the title, else the program's name). Omitted on a save keeps what the draft has.
     */
    letterWording: LetterWordingText.nullable().optional(),
    awardDate: PlainDate.nullable().optional(),
    writeUpText: text(20_000),
    justification: text(20_000),
    /** TAA: let the server assign whole people to pools, or keep the person-by-person choice. */
    fundingMode: z.enum(['auto', 'manual']).optional(),
    /** Who gets the letters. Omitted: the award type's default (Settings › Elections & letters). */
    letters: DraftLetters.optional(),
    lines: z
      .array(DraftLine)
      .max(500)
      .superRefine((lines, ctx) => {
        const seen = new Set<string>();
        lines.forEach((l, i) => {
          if (seen.has(l.personId))
            ctx.addIssue({
              code: 'custom',
              path: [i, 'personId'],
              message: 'Each person can be on an award only once.',
            });
          seen.add(l.personId);
        });
      }),
  })
  .strict();

export const CreateDraft = DraftBody.extend({
  type: AwardTypeCode,
  /** APP-148: a Stock Admin entering this for the sector's current Sector Lead, whom the server finds. */
  forSectorLead: z.literal(true).optional(),
  /** APP-274: a delegate entering this for the Executive who named them. */
  forExecutive: z.uuid().optional(),
}).strict();
export type CreateDraft = z.infer<typeof CreateDraft>;

export const SaveDraft = DraftBody.extend({
  version: z.number().int().positive(),
  /**
   * APP-148: a Stock Admin fixing a Sector Lead's own returned award, for that lead. A save keeps
   * no note or files: the send records what was done for them.
   */
  actingFor: ActingFor.pick({ personId: true }).strict().optional(),
}).strict();
export type SaveDraft = z.infer<typeof SaveDraft>;

export const DraftSaved = z.object({ id: z.uuid(), version: z.number().int() });
export type DraftSaved = z.infer<typeof DraftSaved>;

export const SubmitAward = z
  .object({
    version: z.number().int().positive(),
    /** Over budget: the person chose "Send to Executives" (SL4). Without it, an overage is refused. */
    sendToExecutives: z.boolean().optional(),
    actingFor: ActingFor.optional(),
  })
  .strict();
export type SubmitAward = z.infer<typeof SubmitAward>;

/** PAA ↔ TAA with the same people and nothing re-entered (SL5, SL6). */
export const ConvertAward = z
  .object({
    version: z.number().int().positive(),
    /** APP-148: a Stock Admin converting a Sector Lead's own returned award, for that lead. */
    actingFor: ActingFor.pick({ personId: true }).strict().optional(),
  })
  .strict();
export type ConvertAward = z.infer<typeof ConvertAward>;

export const CancelAward = z
  .object({
    version: z.number().int().positive(),
    reason: z.string().trim().min(1).max(2000),
    actingFor: ActingFor.optional(),
  })
  .strict();
export type CancelAward = z.infer<typeof CancelAward>;

export const ChargeCode = z.object({
  costType: z.enum(['OH', 'GA']),
  account: z.string(),
  /** Text: 15125.110 keeps its zero. */
  project: z.string().nullable(),
  org: z.string(),
});

export const CalculatedLine = z.object({
  person: PersonRef,
  cashCents: Cents,
  options: z.number().int(),
  /** PAA: the total value entered. */
  totalValueCents: Cents.nullable(),
  /**
   * The pool that pays this person. SENT-208: null on a Spot award for a reader who may read neither
   * that pool's budget nor (Payroll) its status, whoever made or approved the award; `paidFrom` and
   * `budget` are then empty too. Never null on any other award type.
   */
  pool: Ref.nullable(),
  coding: ChargeCode.nullable(),
  /**
   * APP-214, a Sector Discretionary Bonus: set when this person is from another sector, null when
   * they're in the bonus's own. `sector` is theirs as the award sees them (live on a draft, as
   * saved once sent). `reason` is why they're on it, null when none was given; it's left out for
   * a reader who isn't shown it (only the Stock Admin team, whoever entered the award and whoever
   * it was entered for are). Absent on other award types and from older servers.
   */
  otherSector: z
    .object({ sector: Ref.nullable(), reason: z.string().nullable().optional() })
    .nullable()
    .optional(),
});
export type CalculatedLine = z.infer<typeof CalculatedLine>;

/** "Paid from": amounts per pool. Never the cash/options fraction (CLAUDE.md). */
export const PaidFrom = z.object({ pool: Ref, cashCents: Cents, options: z.number().int() });

/**
 * Where a pool stands: `not_set` has no budget this year (K34: set it in Executive › Budget
 * pools); `over_budget` has less left than is asked of it (cash or options); `ok` is within it.
 */
export const PoolStatus = z.enum(['not_set', 'ok', 'over_budget']);
export type PoolStatus = z.infer<typeof PoolStatus>;

/**
 * What a reader is told about a pool (APP-111): its `figures`, or its `status` alone. Someone who
 * may start an award but doesn't see budgets (`budget.yearly`), such as a person a System Admin
 * named to start it (APP-106), gets the status: not set, within budget or over budget. That keeps
 * the over-budget warning and "Send to Executives" without the pool's numbers.
 */
export const PoolShows = z.enum(['figures', 'status']);
export type PoolShows = z.infer<typeof PoolShows>;

export const PoolCheck = z.object({
  pool: Ref,
  status: PoolStatus,
  /**
   * `figures`: `remainingAfter` and `shortfall` are the pool's, null only when its budget isn't
   * set. `status`: both are always null, whatever the status (APP-111).
   */
  shows: PoolShows,
  remainingAfter: Units.nullable(),
  shortfall: Units.nullable(),
});
export type PoolCheck = z.infer<typeof PoolCheck>;

/** Something to fix before the award can go: a stable code the UI turns into words. */
export const AwardIssue = z.object({
  code: z.string(),
  personId: z.string().nullable(),
  field: z.string().nullable(),
});
export type AwardIssue = z.infer<typeof AwardIssue>;

export const AwardCalculation = z.object({
  lines: z.array(CalculatedLine),
  paidFrom: z.array(PaidFrom),
  budget: z.array(PoolCheck),
  /** APP-112: false when delegation grants entry without pool-budget visibility. */
  budgetVisible: z.boolean().optional(),
  /**
   * APP-200: the pools this award draws on that the Executives check when they decide, left out
   * of `budget` for this reader: the PAA/TAA pool, on a PAA or TAA, for anyone who doesn't see its
   * budget (a Sector Lead, a delegate, someone named to start one). Their name only, never a
   * figure or status. Absent when there are none, and from older servers.
   */
  checkedByExecutives: z.array(Ref).optional(),
  issues: z.array(AwardIssue),
  /** PAA with six or more people, or TAA with five or fewer (SL5). */
  suggestion: z.enum(['convert_to_taa', 'convert_to_paa']).nullable(),
  /** The FMV the options are valued at, and when it took effect. */
  fmv: z.object({ cents: Cents, effectiveFrom: PlainDate }).nullable(),
  /**
   * APP-151, PAA only: what the amount entered for each person is. `cash`: the cash, with options
   * on top (every PAA since APP-151, drafts included), entered as `cashCents`. `total_value`: an
   * older PAA's total value, split by its saved share, entered as `totalValueCents`. Null for
   * other award types; absent from older servers.
   */
  paaAmount: z.enum(['cash', 'total_value']).nullable().optional(),
  /**
   * APP-151, PAA since APP-151: the value of one option its options are counted in, and when it
   * took effect. Null for other types, older PAAs and while it isn't set; absent from older servers.
   */
  optionValue: z.object({ cents: Cents, effectiveFrom: PlainDate }).nullable().optional(),
});
export type AwardCalculation = z.infer<typeof AwardCalculation>;

/** One person's letter: who it goes to, and whether that was chosen for them alone. */
export const LetterPlanLine = z.object({
  personId: z.uuid(),
  /** "Change for one person": chosen for this person, over the batch choice. */
  chosen: Ref.nullable(),
  /**
   * Who gets this person's letter: their own choice, else the batch choice worked out for them.
   * Null when the batch choice finds no one (no supervisor in the directory, or nobody picked yet).
   */
  recipient: Ref.nullable(),
});

/**
 * Who gets the letters (PRD §11), as the award has it: the batch choice and each person's letter
 * holder. On a sent award the holders are the ones saved when it was sent; the Stock Admin team can
 * still change a holder when releasing the letters.
 */
export const LetterPlan = z.object({
  rule: LetterRecipientRule,
  /** The named holder for `requester` (who entered it), `delegate` and `person`; else null. */
  holder: Ref.nullable(),
  /** The award type's default rule, the choice a new award starts with. */
  typeDefault: LetterDefaultRule,
  lines: z.array(LetterPlanLine),
});
export type LetterPlan = z.infer<typeof LetterPlan>;

/** Plain stages only (CLAUDE.md): Draft → Approval → With the Stock Admin team → … */
export const StageId = z.enum([
  'draft',
  'approval',
  'with_stock_admin',
  'employee_choice',
  'paid',
  'closed',
]);
export const Stage = z.object({
  id: StageId,
  status: z.enum(['done', 'current', 'upcoming', 'skipped']),
  skipReason: z.enum(['within_budget_no_approval_needed', 'no_election']).optional(),
});

export const AwardActions = z.object({
  edit: z.boolean(),
  submit: z.boolean(),
  cancel: z.boolean(),
});

/**
 * Who an award at Approval waits on, never an internal state: the Sector Lead (a delegate's entry),
 * an Executive's decision in the app (PAA, TAA), the Executives outside the app (EDB), or any one
 * Executive for an overage. APP-204: a reader who may not see the budget (APP-112) is told an
 * overage is with the Executives (`executives`), never why.
 */
export const WaitingOn = z.enum([
  'sector_lead',
  /** APP-274: the Executive whose delegate entered it, who asked to approve their entries. */
  'named_executive',
  'executive',
  'executives_outside_app',
  'over_budget',
  'executives',
]);
export type WaitingOn = z.infer<typeof WaitingOn>;

/** APP-201: where the award's letter wording comes from. */
export const LetterWordingSource = z.enum(['entered', 'program', 'title', 'none']);
export type LetterWordingSource = z.infer<typeof LetterWordingSource>;

/** APP-201: the award's letter wording as it stands, and where it comes from. */
export const AwardLetterWording = z.object({
  /** Null when there's nothing to print yet: no program, no name, nothing typed. */
  text: z.string().nullable(),
  source: LetterWordingSource,
});
export type AwardLetterWording = z.infer<typeof AwardLetterWording>;

export const AwardView = z.object({
  /** Computed by the server from current draft facts or its saved submission reason. */
  selfRecognition: z.boolean().optional(),
  id: z.uuid(),
  /** Assigned when first sent; drafts have none. */
  displayId: z.string().nullable(),
  type: OperationalAwardTypeCode,
  title: z.string().nullable(),
  sector: Ref.nullable(),
  program: Ref.nullable(),
  stage: StageId.nullable(),
  /** Set when the award ended before payment. */
  ended: z.enum(['rejected', 'cancelled']).nullable(),
  stages: z.array(Stage),
  createdBy: PersonRef,
  /** A delegate's entry: the Sector Lead it was entered for. */
  onBehalfOf: PersonRef.nullable(),
  awardDate: PlainDate.nullable(),
  writeUpText: z.string().nullable(),
  justification: z.string().nullable(),
  /** APP-201: the letter's wording for everyone on it. Optional: older servers omit it. */
  letterWording: AwardLetterWording.optional(),
  createdAt: z.string(),
  submittedAt: z.string().nullable(),
  version: z.number().int(),
  /** A TAA's pool assignment: worked out by the app, or chosen person by person. Null otherwise. */
  fundingMode: z.enum(['auto', 'manual']).nullable(),
  calculation: AwardCalculation,
  /** Who gets the letters (PRD §11). */
  letters: LetterPlan,
  /** At Approval, whose decision it waits on (SL4: "Waiting on an Executive"); null otherwise. */
  waitingOn: WaitingOn.nullable().optional(),
  /** The budget year it counts in once sent; null for a draft never sent (APP-070). */
  fiscalYear: z.number().int().nullable().optional(),
  /** APP-148 D17: set when the Executive or Sector Lead said "Not what you asked?"; steps refuse while held. */
  held: z.object({ at: z.string(), by: Ref, for: ActForRole }).nullable().optional(),
  /** APP-148: a Stock Admin entered it for a Sector Lead (`onBehalfOf` names the lead). */
  enteredForLead: z.boolean().optional(),
  actions: AwardActions,
});
export type AwardView = z.infer<typeof AwardView>;

export const AwardSummary = z.object({
  id: z.uuid(),
  displayId: z.string().nullable(),
  type: OperationalAwardTypeCode,
  title: z.string().nullable(),
  sector: Ref.nullable(),
  stage: StageId.nullable(),
  ended: z.enum(['rejected', 'cancelled']).nullable(),
  people: z.number().int(),
  /**
   * Who it's for: the first people on it by name, up to three, in the order they were entered
   * (APP-070 D18). With `people`, a list can say "Maya Ortiz, Lucia Ferraro and 1 more". Names
   * only, nothing else about them. Optional: older servers omit it.
   */
  peopleNames: z.array(z.string()).max(3).optional(),
  cashCents: Cents,
  options: z.number().int(),
  /** PAA: the total value entered, which a draft has before its cash and options are worked out. */
  totalValueCents: Cents.nullable(),
  onBehalfOf: Ref.nullable(),
  updatedAt: z.string(),
  /** The organization's day `updatedAt` falls on (APP-018), so lists can show the year when it differs. */
  updatedOn: PlainDate,
  /** The year it counts in once sent (its fiscal year); null for a draft never sent. */
  fiscalYear: z.number().int().nullable(),
  /** At Approval: who it waits on. Null at any other stage. */
  waitingOn: WaitingOn.nullable(),
  /** APP-148 D9: a Stock Admin started it for the Sector Lead `onBehalfOf` names. */
  enteredForLead: z.boolean().optional(),
});
export type AwardSummary = z.infer<typeof AwardSummary>;

/** `GET /awards/mine?year=&page=`: the year to show and which page of what was sent. */
export const MyAwardsQuery = z
  .object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    ...pageFields,
  })
  .strict();
export type MyAwardsQuery = z.infer<typeof MyAwardsQuery>;

/**
 * "My drafts & submitted" (SL2, QA-014), one year at a time. Drafts are work still to send, so
 * they're listed in full with the current year; what was sent is paged, newest first.
 */
export const MyAwards = z.object({
  /** The year shown. */
  year: z.number().int(),
  /** This year in the organization's calendar. */
  currentYear: z.number().int(),
  /** Every year with something in it, newest first, plus the current year. */
  years: z.array(z.object({ year: z.number().int(), count: z.number().int() })),
  /** The sectors these awards are for ("Maritime Airborne"); empty when none has one (EDB). */
  sectors: z.array(Ref),
  /** Open drafts: listed only with the current year. */
  drafts: z.array(AwardSummary),
  /**
   * APP-148 D25: for a Stock Admin while acting for Sector Leads is on, every draft the team
   * started for a Sector Lead and hasn't sent yet, newest first, each with who started it. With
   * them (R63), those sent and returned to the lead: `returned` says who sent it back and why
   * (null for a draft never sent). Listed with the current year only, as `drafts` are.
   */
  forLeadDrafts: z
    .array(
      AwardSummary.extend({
        startedBy: Ref,
        returned: z.object({ by: Ref.nullable(), note: z.string().nullable() }).nullable(),
      }),
    )
    .optional(),
  submitted: z.object({
    items: z.array(AwardSummary),
    page: PageInfo,
  }),
});
export type MyAwards = z.infer<typeof MyAwards>;

/** The Executive pool, a sector's pool, or the PAA/TAA pool. */
/** APP-264: 'spot' is the Spot Awards pool, company-wide, without a sector. */
export const PoolKind = z.enum(['executive', 'sector', 'paa_taa', 'spot']);
export type PoolKind = z.infer<typeof PoolKind>;

/** A pool's position for one year: the numbers on every pool card (SL1, X4). */
export const PoolPosition = z.object({
  pool: Ref,
  /**
   * Which kind of pool it is (Budget pools sends it), so screens never guess from an ID: the
   * PAA/TAA pool's charge codes, the discretionary totals. Optional: older readers omit it.
   */
  kind: PoolKind.optional(),
  fiscalYear: z.number().int(),
  budget: Units.nullable(),
  inProgress: Units,
  paid: Units,
  remaining: Units.nullable(),
});
export type PoolPosition = z.infer<typeof PoolPosition>;

/**
 * The live entry check (`POST /awards/preview`): the calculation, who gets the letters, and where
 * each pool the award can draw on stands before it (shown before anyone is added).
 */
/**
 * A person's recognition in the repeat rule's window, for the entry form's "Recent recognition"
 * (SL3, SL4): the same facts the approval page shows (approvals.ts `RecentRecognition`, QA-006),
 * only for people the person entering may see team recognition for.
 */
export const EntryRecognition = z.object({
  personId: z.string(),
  /** The repeat rule's rolling window, in months ("No awards in 12 months"). */
  months: z.int().positive(),
  awards: z.int().nonnegative(),
  /** Cash plus options at each award's FMV (a PAA's total value), summed. */
  valueCents: Cents,
  /** The latest of those awards. */
  last: z.object({ date: PlainDate, valueCents: Cents }).nullable(),
  /** Already a repeat recipient under the rule, before this award. */
  repeat: z.boolean(),
  /** This award, as it stands, would make them one. */
  wouldBeRepeat: z.boolean(),
});
export type EntryRecognition = z.infer<typeof EntryRecognition>;

/**
 * A pool an award draws on, as the entry form and Recognize show it before anyone is added (SL2,
 * QA-005), for this reader (APP-111): its position and status for someone who sees its figures,
 * or its status alone for someone who doesn't (`PoolShows`).
 */
export const EntryPool = z.discriminatedUnion('shows', [
  PoolPosition.extend({ shows: z.literal('figures'), status: PoolStatus }),
  z.object({
    shows: z.literal('status'),
    pool: Ref,
    kind: PoolKind.optional(),
    fiscalYear: z.number().int(),
    status: PoolStatus,
  }),
]);
export type EntryPool = z.infer<typeof EntryPool>;

/**
 * APP-130: a letter's cash and options mix, the one name for it in every contract: a column of the
 * Settings › Letters grid, a cell's mix, and each person's letter design at entry. The domain's
 * `LetterMix` (`letter-coverage.ts`) is the same three values; a server test keeps them equal.
 * Here, not in `letter-coverage.ts`, because that module imports `letters.ts`, which imports this.
 */
export const LetterMix = z.enum(['cash_only', 'options_only', 'cash_and_options']);
export type LetterMix = z.infer<typeof LetterMix>;

/**
 * Who signs the letters, worked out as the letters step will: from the awarding sector and the
 * giver, never a delegate's own sector. A name and job title only, nothing from the letter library.
 * - `known`: this person signs;
 * - `at_approval`: an Executive Discretionary Bonus asked for by someone who isn't an Executive:
 *   the Executive who approves it signs;
 * - `none`: nobody can sign yet (no Sector Lead, no CEO named, more than one lead). The Stock Admin
 *   team is told when it's sent.
 */
export const EntryLetterSigner = z.discriminatedUnion('status', [
  z.object({ status: z.literal('known'), name: z.string(), jobTitle: z.string().nullable() }),
  z.object({ status: z.literal('at_approval') }),
  z.object({ status: z.literal('none') }),
]);
export type EntryLetterSigner = z.infer<typeof EntryLetterSigner>;

/**
 * One person's letter design at entry (D17): their mix, and what that design reads in the library.
 * - `ready`: their letter can be made;
 * - `sample_to_review`: the Stock Admin team has a letter for the design and a step left on it;
 * - `sample`: APP-212, no real letter for the design yet, so the built-in sample letter is made;
 * - `missing`: no letter for the design yet, or nobody to sign it (D18: a warning, never a block);
 * - `not_yet`: no amount yet, or the Executive who approves it signs.
 * Never a library ID, file name or version.
 */
export const EntryLetterDesign = z.object({
  personId: z.uuid(),
  /** Null until the person has an amount. */
  mix: LetterMix.nullable(),
  state: z.enum(['ready', 'sample_to_review', 'sample', 'missing', 'not_yet']),
  /**
   * A Ready design's sample (`GET /awards/preview/letter-sample`): page 1 of its approved sample,
   * labelled Sample. A path the server built, for the page to fetch as it is; null otherwise.
   */
  sample: z.string().nullable(),
  /**
   * APP-201: a Ready design's sentence around the letter wording (`GET
   * /awards/preview/letter-wording`), a path the server built; null otherwise. Optional: older
   * servers omit it.
   */
  wording: z.string().nullable().optional(),
});
export type EntryLetterDesign = z.infer<typeof EntryLetterDesign>;

/**
 * APP-201: where a Ready design's letter puts the award's wording: the template's own words just
 * before it and just after it, in the same sentence ("…for your participation with " and "."), so
 * the form shows how its wording reads. Other details in that sentence read "…". Null when the
 * letter's words can't be read (a PDF form) or don't print the wording.
 */
export const EntryLetterWording = z.object({
  context: z.object({ before: z.string(), after: z.string() }).nullable(),
});
export type EntryLetterWording = z.infer<typeof EntryLetterWording>;

/**
 * `GET /awards/preview/letter-sample`: the design an entry of this type, for this sector, at this
 * mix resolves to, for the person asking. What the form already holds; the server works out the
 * signer and the letter, and answers only for a Ready design.
 */
export const EntryLetterSampleQuery = z
  .object({
    /** APP-274: a delegate entering for this Executive: it's their letter that's shown. */
    forExecutive: z.uuid().optional(),
    type: AwardTypeCode,
    /** Required except for an Executive Discretionary Bonus, as on the form. */
    sectorId: z.string().min(1).max(100).optional(),
    mix: LetterMix,
    /** The award date on the form, when it has one: the settings in force then decide the mixes. */
    awardDate: PlainDate.optional(),
  })
  .strict();
export type EntryLetterSampleQuery = z.infer<typeof EntryLetterSampleQuery>;

export const EntryPreview = AwardCalculation.extend({
  letters: LetterPlan,
  pools: z.array(EntryPool),
  /** The Executives, any one of whom can approve an overage (SL4). */
  executives: z.array(Ref),
  recent: z.array(EntryRecognition),
  /** APP-130 slice 4: who signs the letters. Optional: older servers omit it. */
  signer: EntryLetterSigner.optional(),
  /** APP-130 slice 4: each person's letter design, in entry order. Optional: older servers omit it. */
  letterDesigns: z.array(EntryLetterDesign).optional(),
});
export type EntryPreview = z.infer<typeof EntryPreview>;

/** One thing this person can start on Recognize (SL2, R2): a type, for a sector, maybe for a lead. */
export const RecognizeEntry = z.object({
  type: AwardTypeCode,
  sector: Ref.nullable(),
  /** A delegate enters for this Sector Lead, or this Executive (`forExecutive`). */
  forPerson: Ref.nullable(),
  /** APP-274: `forPerson` is the Executive who named this person, not a Sector Lead. */
  forExecutive: z.boolean().optional(),
  /**
   * It goes to the Sector Lead first when it's sent: a delegate whose entries need approval, or
   * someone named to start a Sector bonus who neither leads the sector nor enters for its lead
   * (APP-106 review). The rule `submit` routes by (APP-111).
   */
  needsApproval: z.boolean(),
  /**
   * Who approves it first, when `needsApproval`: the lead a delegate enters for, or the sector's
   * current Sector Lead. Null when the sector has no Sector Lead yet: it can't be sent until a
   * System Admin names one. Always null without `needsApproval`.
   */
  approver: Ref.nullable(),
  programs: z.array(Ref),
  /** Who gets the letters, as a new award of this type starts (PRD §11, APP-057). */
  letters: z.object({
    /** The type's default (Settings › Elections & letters). */
    typeDefault: LetterDefaultRule,
    /** "A delegate": the Sector Lead and the sector's delegates, other than you. Empty for EDB. */
    team: z.array(Ref),
  }),
  /**
   * The pools this type draws on for this sector, this year, as the entry form shows them before
   * anyone is added (SL2): the sector pool, the PAA/TAA pool, or both for a TAA, each with its
   * figures or its status alone, as the preview gives them (APP-111). Absent for EDB and from
   * older servers.
   */
  pools: z.array(EntryPool).optional(),
  budgetVisible: z.boolean().optional(),
  /** APP-200: the pools left out of `pools` for this reader, as on `AwardCalculation`. */
  checkedByExecutives: z.array(Ref).optional(),
});
export const RecognizeOptions = z.object({
  entries: z.array(RecognizeEntry),
  /** Everyone can nominate a colleague for a Spot Award when it's switched on (SYS5). */
  spotNomination: z.boolean(),
  /**
   * The people picker and Excel upload also match an exact EE# (APP-057): directory staff, and
   * award creators for the people their awards are for. The EE# itself is never sent back.
   */
  eeNumberSearch: z.boolean(),
  /**
   * APP-148: for a Stock Admin, each sector's lead and the award types they can be entered for.
   * `entries` has one entry per type, as the lead's own Recognize gives it (programs, letters,
   * pools; `forPerson` the lead, no approval first), with the budget as the Stock Admin sees it.
   */
  forSectorLeads: z
    .array(
      z.object({
        sector: Ref,
        lead: ActForPerson,
        types: z.array(AwardTypeCode),
        entries: z.array(RecognizeEntry),
      }),
    )
    .optional(),
});
export type RecognizeOptions = z.infer<typeof RecognizeOptions>;

/** One colleague, as pickers show them: name, title and sector only (APP-022, APP-030). */
export const PersonCard = z.object({
  id: z.uuid(),
  name: z.string(),
  jobTitle: z.string().nullable(),
  sector: Ref.nullable(),
});
export type PersonCard = z.infer<typeof PersonCard>;

/**
 * Colleague search for choosing recipients: names, titles and sectors only (APP-030). `matchedBy`
 * says whether the term was the person's exact EE# (APP-057); the EE# itself is never returned.
 */
export const PeopleLookup = z.object({
  items: z.array(PersonCard.extend({ matchedBy: z.enum(['name', 'ee_number']).optional() })),
});
export type PeopleLookup = z.infer<typeof PeopleLookup>;

// --- Saved groups (SL3 "Saved groups", APP-057) ---------------------------------------------------

/** The most groups one person keeps, and the most people in one (an award's own limit). */
export const SAVED_GROUP_LIMITS = { groups: 50, members: 500 } as const;

export const SavedGroupMember = PersonCard.extend({
  /** Left Areté or hidden from search since the group was saved: kept, but never added again. */
  available: z.boolean(),
});
export type SavedGroupMember = z.infer<typeof SavedGroupMember>;

/** A named list of people one person keeps for entering awards. Private to its owner. */
export const SavedGroup = z.object({
  id: z.uuid(),
  name: z.string(),
  members: z.array(SavedGroupMember),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type SavedGroup = z.infer<typeof SavedGroup>;
export const SavedGroups = z.object({ items: z.array(SavedGroup) });
export type SavedGroups = z.infer<typeof SavedGroups>;

const uniqueIds = (ids: readonly string[], ctx: z.RefinementCtx) => {
  const seen = new Set<string>();
  ids.forEach((id, i) => {
    if (seen.has(id))
      ctx.addIssue({ code: 'custom', path: [i], message: 'Each person is in a group only once.' });
    seen.add(id);
  });
};

export const SaveGroup = z
  .object({
    name: z.string().trim().min(1).max(80),
    memberIds: z.array(z.uuid()).min(1).max(SAVED_GROUP_LIMITS.members).superRefine(uniqueIds),
  })
  .strict();
export type SaveGroup = z.infer<typeof SaveGroup>;

export const UpdateGroup = SaveGroup.extend({ version: z.number().int().positive() }).strict();
export type UpdateGroup = z.infer<typeof UpdateGroup>;

export const DeleteGroup = z.object({ version: z.number().int().positive() }).strict();
export type DeleteGroup = z.infer<typeof DeleteGroup>;

// --- Add people from Excel or CSV (SL3 "Upload Excel", APP-057) ----------------------------------

/** The upload's limits: a sheet bigger than an award can hold is refused, not cut short. */
export const PEOPLE_SHEET_LIMITS = { bytes: 1024 * 1024, rows: 500, columns: 50 } as const;

/** Where each row of a sheet landed. Nothing is added until the person confirms. */
export const PeopleSheetRowStatus = z.enum([
  /** One active person matched. */
  'matched',
  /** No one matched the EE# or the name. */
  'not_found',
  /** The name matches more than one person: search for them instead. */
  'ambiguous',
  /** Matched someone who has left Areté. */
  'inactive',
  /** The same person on an earlier row. */
  'repeated',
  /** An amount that isn't a whole number of options or a dollar amount. */
  'bad_amount',
]);
export type PeopleSheetRowStatus = z.infer<typeof PeopleSheetRowStatus>;

export const PeopleSheetRow = z.object({
  /** The row number in the sheet (the header is row 1). */
  row: z.number().int(),
  /** What the sheet said, as typed there, to recognize the row by. */
  given: z.object({ eeNumber: z.string().nullable(), name: z.string().nullable() }),
  status: PeopleSheetRowStatus,
  person: PersonCard.nullable(),
  matchedBy: z.enum(['ee_number', 'name']).nullable(),
  /** The amounts read for this award type; null where the sheet had none. */
  amounts: z.object({
    cashCents: Count.nullable(),
    options: Count.nullable(),
    totalValueCents: Count.nullable(),
  }),
});
export type PeopleSheetRow = z.infer<typeof PeopleSheetRow>;

export const PeopleSheetMatch = z.object({
  fileName: z.string(),
  /** The header each recognized column had in the sheet; null when the sheet had none. */
  columns: z.object({
    eeNumber: z.string().nullable(),
    name: z.string().nullable(),
    cash: z.string().nullable(),
    options: z.string().nullable(),
    totalValue: z.string().nullable(),
  }),
  /** Headers the upload didn't use. */
  ignored: z.array(z.string()),
  rows: z.array(PeopleSheetRow),
});
export type PeopleSheetMatch = z.infer<typeof PeopleSheetMatch>;

/** The upload's own fields, sent with the file. */
export const PeopleSheetFields = z
  .object({
    type: AwardTypeCode,
    sectorId: z.string().min(1).max(64).optional(),
    /** APP-148: matched for a Stock Admin entering the award for the sector's Sector Lead. */
    forSectorLead: z.literal('true').optional(),
  })
  .strict();
export type PeopleSheetFields = z.infer<typeof PeopleSheetFields>;

/** One change in words: the field's plain name, and its value before and after (APP-065). */
export const TrailChange = z.object({
  label: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
});
export type TrailChange = z.infer<typeof TrailChange>;

/**
 * One change on the award record's trail (G1): who, when, what, before and after. Field labels are
 * plain words; a person's line names the person. Split changes never appear here (CLAUDE.md).
 * `what` and `change` are the server's words for it (APP-065), so the record and its PDF export say
 * the same thing; `before` and `after` keep the saved values.
 */
export const AwardHistoryEntry = z.object({
  id: z.uuid(),
  at: z.string(),
  actor: z.object({
    name: z.string(),
    onBehalfOf: z.string().nullable(),
    /** The roles they acted in then ("Stock Admin", "Sector Lead · Maritime Airborne"). */
    roles: z.array(z.string()),
    /** APP-122: who was signed in, when a tester acted as them in Preview as. */
    previewedBy: z.string().nullable().optional(),
  }),
  source: z.string(),
  /** `view`: the record was exported (APP-065). `notice`: MyRewards told someone (APP-069). */
  action: z.enum(['create', 'update', 'delete', 'restore', 'view', 'notice']),
  /**
   * What the change was about: the award itself, one person's line, one of its files, or the
   * budget ledger's entry made for it (R95: its top-up, or the Reduction a return took back).
   */
  subject: z.enum(['award', 'line', 'file', 'budget']),
  /** For a line: whose it is. */
  person: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** "SDB-2026-0158 · Payload milestone", or a file's "… · nomination.docx". */
  label: z.string(),
  field: z.string().nullable(),
  before: z.unknown(),
  after: z.unknown(),
  reason: z.string().nullable(),
  /** One sentence: what happened ("Approved the award."). */
  what: z.string(),
  /** The change in words, when there is one to show. */
  change: TrailChange.nullable(),
  /**
   * APP-148 R44: the note the Stock Admin kept with an action done for someone, on that action's
   * own line, for those who may read what was done for that person (Executives, the Stock Admin
   * team, System Admins and the person it was for); null for anyone else and on every other line.
   * Optional: older servers omit it.
   */
  note: z.string().nullable().optional(),
});

/** Who moved an award, and when: from the trail entry that did it. */
export const StageEvent = z.object({
  at: z.string(),
  /** Null when that entry isn't one this person may read (Payroll's, for example). */
  by: z.object({ name: z.string(), onBehalfOf: z.string().nullable() }).nullable(),
});
export type StageEvent = z.infer<typeof StageEvent>;

/**
 * "Where it is" with who and when (G1, QA-020, APP-065), from the trail this person may read:
 * the latest entry that put the award in each stage and the latest that moved it on. A stage it
 * hasn't reached, or whose entry this person can't read, has none.
 */
export const AwardStageMark = z.object({
  id: StageId,
  entered: StageEvent.nullable(),
  ended: StageEvent.nullable(),
});
export type AwardStageMark = z.infer<typeof AwardStageMark>;

export const AwardHistory = z.object({
  items: z.array(AwardHistoryEntry),
  /** One mark per stage, in stage order. */
  stages: z.array(AwardStageMark),
  /** Employee's choice: the last day anyone still choosing has (an instant), or null. */
  electionDue: z.string().nullable(),
});
export type AwardHistory = z.infer<typeof AwardHistory>;
